import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Images, Loader2, MapPin, Search, Folder } from "lucide-react";
import { toast } from "sonner";

// sem acento e sem separadores: "São Luís" acha "07-CCF-Sao-Luis.png"
const semAcento = (t) => (t || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
const fileUrl = (v) => `${(api.defaults.baseURL || "/api").replace(/\/$/, "")}/admin/videos/${v.id}/download`;

/** Imagens da biblioteca; as que têm o nome da cidade (no nome do arquivo ou da pasta) vêm separadas. */
function useLibraryImages(city) {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/admin/videos").then((r) => setD(r.data)).catch(() => setD({ videos: [], folders: [] }));
  }, []);
  return useMemo(() => {
    if (!d) return { all: null, daCidade: [] };
    const pastas = Object.fromEntries((d.folders || []).map((f) => [f.id, f.name]));
    const all = d.videos.filter((v) => v.kind === "image").map((v) => ({ ...v, pasta: pastas[v.folder_id] || "" }));
    const c = semAcento(city).trim();
    const daCidade = c.length >= 3 ? all.filter((v) => semAcento(`${v.name} ${v.pasta}`).includes(c)) : [];
    return { all, daCidade };
  }, [d, city]);
}

function Thumb({ v, onPick, busy }) {
  return (
    <button type="button" onClick={() => onPick(v)} disabled={!!busy} title={v.name}
      className="group relative text-left rounded-lg overflow-hidden border border-slate-200 hover:border-[#1E9E2A] hover:shadow-md transition disabled:opacity-60" data-testid={`cover-pick-${v.id}`}>
      <img src={fileUrl(v)} alt="" loading="lazy" className="w-full aspect-[3/2] object-cover bg-slate-100" />
      <div className="px-2 py-1 text-[11px] text-slate-600 truncate">{v.name}</div>
      {busy === v.id && <div className="absolute inset-0 grid place-items-center bg-white/70"><Loader2 className="h-5 w-5 animate-spin text-[#1E9E2A]" /></div>}
    </button>
  );
}

/**
 * Capa do curso a partir da Biblioteca: mostra logo abaixo as imagens com o nome da cidade
 * e um botão para escolher qualquer imagem. onPick(url pública).
 */
export function LibraryCover({ city = "", onPick }) {
  const { all, daCidade } = useLibraryImages(city);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");
  const escolher = async (v) => {
    setBusy(v.id);
    try { const r = await api.post(`/admin/library/${v.id}/public`); onPick(r.data.url); setOpen(false); toast.success("Capa escolhida da biblioteca."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  const busca = semAcento(q);
  const lista = (all || []).filter((v) => !busca || semAcento(`${v.name} ${v.pasta}`).includes(busca));
  const cidade = city.trim();

  return (
    <div className="space-y-2" data-testid="library-cover">
      {cidade && daCidade.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-2.5">
          <div className="text-xs font-semibold text-[#B45309] flex items-center gap-1 mb-2"><MapPin className="h-3.5 w-3.5" /> Imagens de {cidade} na biblioteca — clique para usar como capa</div>
          <div className="grid grid-cols-3 gap-2">
            {daCidade.slice(0, 3).map((v) => <Thumb key={v.id} v={v} onPick={escolher} busy={busy} />)}
          </div>
          {daCidade.length > 3 && <button type="button" onClick={() => { setQ(cidade); setOpen(true); }} className="text-xs text-[#B45309] font-semibold mt-1.5 hover:underline">Ver todas ({daCidade.length})</button>}
        </div>
      )}
      {cidade && all && daCidade.length === 0 && (
        <p className="text-[11px] text-slate-400">Nenhuma imagem com "{cidade}" no nome na biblioteca. Dica: crie uma pasta com o nome da cidade e envie as capas nela.</p>
      )}
      <Button type="button" variant="outline" size="sm" onClick={() => { setQ(""); setOpen(true); }} data-testid="cover-library-btn"><Images className="h-4 w-4 mr-1.5" /> Escolher da biblioteca</Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-display">Escolher capa da biblioteca</DialogTitle>
            <DialogDescription>Clique na imagem para usar como capa do curso.</DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pesquisar por nome da imagem ou da pasta (ex.: cidade)..." className="pl-9 h-10" autoFocus />
          </div>
          <div className="max-h-[60vh] overflow-y-auto iep-scroll">
            {all === null ? <div className="p-8 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
              : lista.length === 0 ? <p className="p-8 text-center text-sm text-slate-500">{all.length ? "Nenhuma imagem com essa busca." : "A biblioteca não tem imagens ainda. Envie em Cursos → Biblioteca."}</p>
                : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {lista.map((v) => (
                      <div key={v.id}>
                        <Thumb v={v} onPick={escolher} busy={busy} />
                        {v.pasta && <div className="text-[10px] text-slate-400 flex items-center gap-1 mt-0.5 px-1"><Folder className="h-3 w-3" /> {v.pasta}</div>}
                      </div>
                    ))}
                  </div>
                )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
